import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import Table from 'cccisd-graphql-table';
import Modal from 'cccisd-modal';
import Loader from 'cccisd-loader';
import Tooltip from 'cccisd-tooltip';
import { Link } from 'cccisd-react-router';
import notification from 'cccisd-notification';
import Notes from '../Notes';
import { setSelectedStudent } from 'js/reducers/assessments';
import HiddenBlock from 'cccisd-hidden-block';
import _get from 'lodash/get';
import { Formik, Form, Field, CccisdInput, CccisdSelect } from 'cccisd-formik';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import Component from '../../../vendor/reports/reportTemplates/result/Player';
import EditStudentForm, { gradeOptions } from './EditStudentForm';
import style from './style.css';
// Queries
import studentsQuery from './studentsTable.graphql';
import studentsQueryArchived from './studentsTableArchived.graphql';
import getNumberOfStudentsQuery from './getNumberOfStudents.graphql';
import getInstructorStateAndSchoolsQuery from './getInstructorStateAndSchools.graphql';
import getProviderFieldsQuery from './getProviderFields.graphql';
import parentDeploymentIdQuery from './parentDeploymentId.graphql';
// Icons
import IconQuill from 'cccisd-icons/quill3';
import IconPlus from 'cccisd-icons/plus2';
import IconEdit from 'cccisd-icons/pencil7';
import IconClipboard from 'cccisd-icons/clipboard2';
import IconArchive from 'cccisd-icons/box-add';
import IconUnarchive from 'cccisd-icons/box-remove';
import ShareLogo from '../../../components/Header/SHARE-Logo.svg';

const Fortress = window.cccisd.fortress;
const Boilerplate = window.cccisd.boilerplate;
const creatingPawnId = Fortress.user.acting.id;
const href = window.location.href;

const Reports = window.cccisd.appDefs.reports.list;

export const colors = {
    green: '#2c817c',
    blue: '#154988',
    yellow: '#fcbd2b',
    red: '#9a1d22',
    lightBlue: '#0d6efd',
};

const DeploymentCenter = props => {
    const [loading, setLoading] = useState(true);
    const [studentCount, setStudentCount] = useState(-1);
    const [schoolList, setSchoolList] = useState([]);
    const [instructorStateAndSchool, setInstructorStateAndSchool] = useState({});
    const [welcomeTourShown, setWelcomeTourShown] = useState(false);
    const [deploymentId, setDeploymentId] = useState(-1);
    const [parentDeploymentId, setParentDeploymentId] = useState(false);
    const [welcomeTourSurveyId, setWelcomeTourSurveyId] = useState('');

    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const archivedTableRef = useRef(null);
    const welcomeTourModalRef = useRef(null);
    const welcomeTourSurveyModalRef = useRef(null);
    const welcomeTourReportModalRef = useRef(null);
    const welcomeTourCompleteModalRef = useRef(null);

    useEffect(() => {
        setLoading(true);

        // used to show or hide the instructional "add students here" arrow & text when there are no students yet
        const getNumberOfStudents = async () => {
            const response = await client.query({
                query: getNumberOfStudentsQuery,
                fetchPolicy: 'network-only',
                variables: {
                    instructorPawnId: creatingPawnId,
                },
            });

            let val = _get(response, 'data.roles.totalLearners', 0);
            setStudentCount(val);
        };

        // used to populate schools that an instructor can select for new students
        const getInstructorStateAndSchools = async () => {
            const response = await client.query({
                query: getInstructorStateAndSchoolsQuery,
                fetchPolicy: 'network-only',
            });

            let schools = _get(response, 'data.groups.siteList', []).map(val => val.group);
            setSchoolList(schools);

            setInstructorStateAndSchool(_get(response, 'data.groups.organization', {}));
        };

        getNumberOfStudents();
        getInstructorStateAndSchools();

        setLoading(false);
    }, []);

    useEffect(() => {
        (async () => {
            const welcomeTourShownResponse = await getWelcomeTourShown();

            if (!welcomeTourShownResponse) {
                const parentDeploymentResp = await client.query({
                    query: parentDeploymentIdQuery,
                    fetchPolicy: 'network-only',
                });
                const parentId = _get(parentDeploymentResp, 'data.flows.deployment.deploymentId');
                const surveyId = _get(parentDeploymentResp, 'data.flows.deployment.assignment.survey.surveyId', null);

                if (parentId && parentId >= 1) {
                    setParentDeploymentId(parentId); // triggers next useEffect hook
                    setWelcomeTourSurveyId(surveyId);
                }
            }
        })();
    }, []);

    useEffect(() => {
        if (!welcomeTourShown && parentDeploymentId && parentDeploymentId > 0) {
            (async () => {
                setLoading(true);

                forceNextChildDeployment();

                if (welcomeTourModalRef.current) {
                    welcomeTourModalRef.current.open();
                }

                setLoading(false);
            })();
        }
    }, [parentDeploymentId]);

    useEffect(() => {
        const studentSync = async () => {
            try {
                const response = await axios.get('/api/studentSync');
                if (response?.data?.status === 'success' && response?.data?.data.refresh === true) {
                    window.location.reload();
                }
            } catch (error) {
                console.error('StudentSync API Error:', error);
            }
        };

        studentSync();
    }, []);

    async function getWelcomeTourShown() {
        const response = await client.query({
            query: getProviderFieldsQuery,
            fetchPolicy: 'network-only',
            variables: {
                pawnId: creatingPawnId,
            },
        });

        let welcomeTourShownResponse = _get(response, 'data.roles.instructor.fields.welcomeTourShown', false);

        setWelcomeTourShown(welcomeTourShownResponse);

        return welcomeTourShownResponse;
    }

    async function resetWelcomeTour() {
        await axios.put(
            Boilerplate.route('api.nexus.pawn.update', {
                pawn: creatingPawnId,
            }),
            { pawnHash: Fortress.user.acting.random_hash, welcomeTourShown: false }
        );

        window.location = Boilerplate.url('/students');
    }

    async function forceNextChildDeployment() {
        let repeatedDeploymentResp = await axios.get(
            Boilerplate.route('api.assignmentDeployment.repeated', {
                deploymentId: parentDeploymentId,
                pawnId: creatingPawnId,
                hash: Fortress.user.acting.respondent_hash,
            }),
            // passing `true` or nothing because backend interprets `false` as truthy string
            { params: { forceRepeat: true } }
        );

        const responseDeploymentId = _get(repeatedDeploymentResp, 'data.data.deploymentId');

        setDeploymentId(responseDeploymentId || -1);
    }

    // used as default values when a new student is being created
    const getStudentDefaultValues = () => {
        const defaultValues = {
            group: schoolList.length === 1 ? schoolList[0].groupId : '',
            state: _get(instructorStateAndSchool, 'group.groupId', null),
            district: Fortress.user.acting.group_id,
            role: 'learner',
            parentPawns: creatingPawnId,
        };

        return defaultValues;
    };

    const AddStudentsButton = () => {
        return (
            <Modal
                trigger={
                    <button className="btn btn-primary" style={{ marginTop: '20px' }} type="button">
                        <>
                            <IconPlus spaceRight />
                            Add Students
                        </>
                    </button>
                }
                size="medium"
                title="Add New Student"
                render={({ closeModal }) => {
                    return (
                        <Formik
                            initialValues={getStudentDefaultValues()}
                            validate={values => {
                                const errors = {};

                                if (!('group' in values) || !values.group) {
                                    errors.group = 'School is required';
                                }
                                if (!('firstName' in values) || !values.firstName) {
                                    errors.firstName = 'First Name is required';
                                }
                                if (!('lastName' in values) || !values.lastName) {
                                    errors.lastName = 'Last Name is required';
                                }

                                return errors;
                            }}
                            validateOnChange={false}
                            validateOnBlur={false}
                            onSubmit={async values => {
                                const pawnResponse = await axios.post(
                                    Boilerplate.route('api.nexus.pawn.store'),
                                    values
                                );

                                let ok =
                                    pawnResponse.status === 200 && _get(pawnResponse, 'data.status', '') === 'success';

                                if (ok) {
                                    notification({
                                        message: 'Student was added!',
                                    });
                                } else {
                                    notification({
                                        message: 'Error adding student',
                                        type: 'danger',
                                    });
                                }

                                setStudentCount(studentCount + 1);
                                tableRef.current.loadData();
                                closeModal();
                            }}
                            render={({ values, errors }) => {
                                return (
                                    <Form>
                                        <div style={{ minHeight: '270px' }}>
                                            <label className={style.required}>
                                                <b>School:</b>
                                            </label>
                                            <Field
                                                name="group"
                                                className={style.required}
                                                component={CccisdSelect}
                                                options={[{ value: '', label: '--Please select a School--' }].concat(
                                                    schoolList.map(school => {
                                                        return {
                                                            value: school.groupId,
                                                            label: school.label,
                                                        };
                                                    })
                                                )}
                                            />
                                            <label className={style.required}>
                                                <b>First Name:</b>
                                            </label>
                                            <Field name="firstName" component={CccisdInput} />
                                            <label className={style.required}>
                                                <b>Last Name:</b>
                                            </label>
                                            <Field name="lastName" component={CccisdInput} />
                                            <label>
                                                <b>Email:</b>
                                            </label>
                                            <Field name="email" component={CccisdInput} type="email" />
                                            <label>
                                                <b>Student ID:</b>
                                            </label>
                                            <Field name="studentId" component={CccisdInput} />
                                            <label>
                                                <b>Grade:</b>
                                            </label>
                                            <Field name="grade" component={CccisdSelect} options={gradeOptions} />
                                        </div>
                                        <div className={style.submit_button_container}>
                                            <button className="btn btn-primary" type="submit">
                                                Submit
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        />
                    );
                }}
            />
        );
    };

    const InstructionalArrow = () => {
        return (
            <div className={style.arrow_container} hidden={studentCount !== 0}>
                <svg className={style.svg} viewBox="140 75 70 80" preserveAspectRatio="none">
                    <defs>
                        <marker
                            id="arrowhead"
                            markerWidth="20"
                            markerHeight="20"
                            refX="6"
                            refY="10"
                            orient="auto"
                            markerUnits="userSpaceOnUse"
                        >
                            <polygon points="0 0, 20 10, 0 20" />
                        </marker>
                    </defs>

                    <path d="M 210 150 L 150 150 L 150 90" strokeWidth="4" fill="none" markerEnd="url(#arrowhead)" />
                </svg>
                <div className={style.arrow_text}>
                    <h5>Add a student to get started</h5>
                </div>
            </div>
        );
    };

    const ResultsCell = ({ row }) => {
        let numPlansWithCompletedSurveys = 0;

        if (row.completed && Array.isArray(row.completed)) {
            numPlansWithCompletedSurveys = row.completed.reduce((count, assignmentPlan) => {
                if (assignmentPlan.completed === true) {
                    return count + 1;
                }

                // If not completed, check if any survey has progress status of 2
                if (assignmentPlan.sessionList && Array.isArray(assignmentPlan.sessionList)) {
                    for (const session of assignmentPlan.sessionList) {
                        if (session.assignmentProgressList && Array.isArray(session.assignmentProgressList)) {
                            for (const assignmentProgress of session.assignmentProgressList) {
                                if (assignmentProgress.surveyProgressStatus) {
                                    for (const [key, value] of Object.entries(
                                        assignmentProgress.surveyProgressStatus
                                    )) {
                                        if (key.startsWith('survey') && !key.endsWith('_lastVisit') && value === 2) {
                                            return count + 1;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                return count;
            }, 0);
        }

        const name = row.fullName || 'this student';
        const tooltip =
            numPlansWithCompletedSurveys === 0
                ? `No results are available for ${name}. Administer at least one assessment to see results.`
                : `View and download results. Results available for ${name}: ${numPlansWithCompletedSurveys}`;

        return (
            <Tooltip title={tooltip}>
                <Link
                    to={`/results/${row['pawn.pawnId']}`}
                    className={`btn btn-sm ${style.linkBtn} ${style.resultsBtn} ${
                        numPlansWithCompletedSurveys === 0 ? style.disabled : ''
                    }`}
                    onClick={
                        numPlansWithCompletedSurveys === 0
                            ? e => e.preventDefault()
                            : () => dispatch(setSelectedStudent(row))
                    }
                >
                    <IconClipboard spaceRight />
                    Results
                    {numPlansWithCompletedSurveys > 0 && <span>&nbsp;({numPlansWithCompletedSurveys})</span>}
                </Link>
            </Tooltip>
        );
    };

    const StudentsTable = () => {
        return (
            <Table
                ref={tableRef}
                query={studentsQuery}
                graphqlVariables={{ instructorPawnId: Fortress.user.acting.id }}
                renders={{
                    actions: ({ value, rows, row, loadData }) => {
                        return (
                            <div className={style.action_buttons_container}>
                                <Tooltip title="Edit student's information.">
                                    <Modal
                                        trigger={
                                            <button
                                                className="btn btn-sm btn-success d-flex align-items-center"
                                                type="button"
                                            >
                                                <IconEdit />
                                            </button>
                                        }
                                        size="medium"
                                        title="Edit Student"
                                        render={({ closeModal }) => {
                                            return (
                                                <EditStudentForm
                                                    row={row}
                                                    closeModal={closeModal}
                                                    loadData={loadData}
                                                    schoolList={schoolList}
                                                />
                                            );
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title={
                                        row['fields.centerventionId']
                                            ? ''
                                            : 'Move student to archive. This action is reversible.'
                                    }
                                >
                                    <button
                                        className="btn btn-sm btn-primary d-flex align-items-center"
                                        type="button"
                                        disabled={row['fields.centerventionId']}
                                        onClick={() => {
                                            const archiveStudent = async () => {
                                                const response = await axios.put(
                                                    Boilerplate.route('api.nexus.pawn.archive', {
                                                        pawn: row['pawn.pawnId'],
                                                    }),
                                                    {
                                                        pawnHash: row['pawn.pawnHash'],
                                                    }
                                                );

                                                if (response?.data?.status === 'success') {
                                                    notification('Student was archived!');
                                                    loadData();
                                                    archivedTableRef.current.loadData();
                                                } else {
                                                    notification({
                                                        message: 'An error occured archiving student',
                                                        type: 'danger',
                                                    });
                                                }
                                            };

                                            archiveStudent();
                                        }}
                                    >
                                        <IconArchive />
                                    </button>
                                </Tooltip>
                            </div>
                        );
                    },
                    administer: ({ row }) => {
                        let numIncomplete = row.completed.reduce((total, assignmentPlan) => {
                            const stopped = JSON.parse(assignmentPlan.settings || '{}').stopAssessing;
                            if (assignmentPlan.completed !== true && !stopped) {
                                total++;
                            }
                            return total;
                        }, 0);

                        const name = row.fullName || 'this student';
                        const tooltip =
                            numIncomplete === 0
                                ? `There are no assessments assigned for ${name}. Click here to assign assessments.`
                                : `View and administer assigned assessments. Assessments available for ${name}: ${numIncomplete}`;

                        return (
                            <Tooltip title={tooltip} maxWidth="500px">
                                <Link
                                    to={`/administer/${row['pawn.pawnId']}`}
                                    className={`btn btn-sm ${style.linkBtn} ${style.administerBtn}`}
                                    onClick={() => dispatch(setSelectedStudent(row))}
                                >
                                    <IconQuill spaceRight />
                                    Administer
                                    {numIncomplete > 0 && <span>&nbsp;({numIncomplete})</span>}
                                </Link>
                            </Tooltip>
                        );
                    },
                    results: ({ row }) => {
                        return <ResultsCell row={row} />;
                    },
                }}
                columns={[
                    {
                        name: 'actions',
                        label: 'Actions',
                        render: 'actions',
                        hideInCsv: true,
                    },
                    {
                        name: `fields.firstName`,
                        label: 'First Name',
                        filter: true,
                        sort: true,
                        className: style.cellCentered,
                    },
                    {
                        name: `fields.lastName`,
                        label: 'Last Name',
                        filter: true,
                        sort: true,
                        className: style.cellCentered,
                    },
                    {
                        name: `fields.studentId`,
                        label: 'Student ID',
                        filter: true,
                        sort: true,
                        className: style.cellCentered,
                    },
                    {
                        name: `fields.grade`,
                        label: 'Grade',
                        filter: true,
                        sort: true,
                        width: '80px',
                        className: style.cellCentered,
                    },
                    {
                        name: `groupName`,
                        label: 'School',
                        filter: true,
                        sort: true,
                        className: style.cellCentered,
                    },
                    {
                        name: 'notes',
                        label: 'Student Notes',
                        width: '130px',
                        className: style.cellCentered,
                        render: v => <Notes {...v} />,
                    },
                    {
                        name: 'selectedAssignmentPlanCount_incomplete',
                        label: 'Administer',
                        render: 'administer',
                    },
                    {
                        name: 'selectedAssignmentPlanCount_complete',
                        label: 'Results',
                        render: 'results',
                    },
                    {
                        name: `completed.completedDate`,
                        label: 'Most Recent Completed',
                        className: style.cellCentered,
                        render: val => {
                            let count = val.row.completed.filter(
                                completedVal => completedVal.completed && completedVal.completedDate
                            );

                            return <>{_get(count, '[0].completedDate', '')}</>;
                        },
                    },
                ]}
                perPage={25}
                rowKey="pawn.pawnId"
                orderBy="fields.firstName"
            />
        );
    };

    const ArchivedStudentsTable = () => {
        return (
            <HiddenBlock title="Show archived students">
                <Table
                    ref={archivedTableRef}
                    query={studentsQueryArchived}
                    graphqlVariables={{ instructorPawnId: Fortress.user.acting.id }}
                    renders={{
                        actions: ({ row, loadData }) => {
                            return (
                                <div className={style.unarchive_button_container}>
                                    <Tooltip title="Unarchive">
                                        <button
                                            className="btn btn-info d-flex align-items-center"
                                            type="button"
                                            onClick={() => {
                                                const unarchiveStudent = async () => {
                                                    const response = await axios.put(
                                                        Boilerplate.route('api.nexus.pawn.unarchive', {
                                                            pawn: row['pawn.pawnId'],
                                                        }),
                                                        {
                                                            pawnHash: row['pawn.pawnHash'],
                                                        }
                                                    );

                                                    if (response?.data?.status === 'success') {
                                                        notification('Student was unarchived!');
                                                        loadData();
                                                        tableRef.current.loadData();
                                                    } else {
                                                        notification({
                                                            message: 'An error occured unarchiving student',
                                                            type: 'danger',
                                                        });
                                                    }
                                                };

                                                unarchiveStudent();
                                            }}
                                        >
                                            <IconUnarchive />
                                        </button>
                                    </Tooltip>
                                </div>
                            );
                        },
                    }}
                    columns={[
                        {
                            name: 'actions',
                            label: 'Actions',
                            render: 'actions',
                            hideInCsv: true,
                        },
                        {
                            name: `fields.firstName`,
                            label: 'First Name',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                        {
                            name: `fields.lastName`,
                            label: 'Last Name',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                        {
                            name: `fields.studentId`,
                            label: 'Student ID',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                        {
                            name: `fields.grade`,
                            label: 'Grade',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                        {
                            name: `groupName`,
                            label: 'School',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                        {
                            name: `incomplete`,
                            label: 'Administered',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                            width: '60px',
                            render: val => {
                                let count = val.row.completed.filter(
                                    completedVal => completedVal.completed !== true
                                ).length;
                                return <>{count}</>;
                            },
                        },
                        {
                            name: `completed.completed`,
                            label: 'Results',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                            width: '60px',
                            render: val => {
                                let count = val.row.completed.filter(
                                    completedVal => completedVal.completed === true
                                ).length;
                                return <>{count}</>;
                            },
                        },
                        {
                            name: `completed.completedDate`,
                            label: 'Most Recent Completed',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                            render: val => {
                                let count = val.row.completed.filter(
                                    completedVal => completedVal.completed && completedVal.completedDate
                                );

                                return <>{_get(count, '[0].completedDate', '')}</>;
                            },
                        },
                        {
                            name: `pawn.archivedDate`,
                            label: 'Archived Date',
                            filter: true,
                            sort: true,
                            className: style.cellCentered,
                        },
                    ]}
                    perPage={25}
                    rowKey="pawn.pawnId"
                    orderBy="fields.lastName"
                />
            </HiddenBlock>
        );
    };

    const setWelcomeTourFieldAs = async val => {
        const pawnUpdateResponse = await axios.put(
            Boilerplate.route('api.nexus.pawn.update', {
                pawn: creatingPawnId,
            }),
            { pawnHash: Fortress.user.acting.random_hash, welcomeTourShown: val }
        );

        let ok = pawnUpdateResponse.status === 200 && _get(pawnUpdateResponse, 'data.status', '') === 'success';

        setWelcomeTourShown(true);

        return ok;
    };

    const ShowWelcomeTour = () => {
        const subtext1 =
            'See an example of what SHARE can do. Complete a brief assessment and generate a sample report based on your answers.';

        let skipped = true;

        if (welcomeTourShown) {
            return <></>;
        }

        return (
            <Modal
                ref={welcomeTourModalRef}
                modalClassName={style.btn_close_wrapper}
                size="600px"
                afterClose={() => {
                    if (skipped) {
                        const ok = setWelcomeTourFieldAs(true);

                        if (ok) {
                            notification({
                                message:
                                    'You can visit the SHARE Help Center at any time to complete an assessment and see a sample results report.',
                                duration: 0,
                            });
                        }
                    }
                }}
                render={({ closeModal }) => {
                    return (
                        <div style={{ padding: '10%', paddingTop: '0px' }}>
                            <div className={style.welcome_to}>Welcome to</div>
                            <img src={ShareLogo} alt="Logo" />
                            <div className={style.welcome_subtext}>
                                <div>{subtext1}</div>
                            </div>

                            <div className={style.start_button}>
                                <button
                                    className="btn btn-primary btn-lg"
                                    type="button"
                                    onClick={() => {
                                        skipped = false;
                                        welcomeTourSurveyModalRef.current.open();
                                        closeModal();
                                    }}
                                >
                                    Get Started
                                </button>
                            </div>
                        </div>
                    );
                }}
            />
        );
    };

    const ShowWelcomeTourSurveyModal = () => {
        let skipped = true;

        if (welcomeTourShown) {
            return <></>;
        }

        return (
            <Modal
                ref={welcomeTourSurveyModalRef}
                modalClassName={style.btn_close_wrapper_survey}
                size="85vw"
                beforeClose={() => {
                    if (skipped) {
                        const ok = setWelcomeTourFieldAs(true);

                        if (ok) {
                            notification({
                                message:
                                    'You can visit the SHARE Help Center at any time to complete an assessment and see a sample results report.',
                                duration: 0,
                            });
                        }
                    }
                }}
                render={({ closeModal }) => {
                    return (
                        <div style={{ maxHeight: '85vh', overflow: 'scroll' }}>
                            <DeploymentPlayer
                                deploymentId={deploymentId}
                                pawnId={Fortress.user.acting.id}
                                pawnHash={Fortress.user.acting.random_hash}
                                onComplete={() => {
                                    skipped = false;
                                    closeModal();
                                    welcomeTourReportModalRef.current.open();
                                }}
                            />
                        </div>
                    );
                }}
            />
        );
    };

    const ShowWelcomeTourReportModal = () => {
        const reportJSON = Reports.find(item => item.id === 'mscs_provider');

        return (
            <Modal
                ref={welcomeTourReportModalRef}
                size="min-content"
                beforeShow={() => {
                    setWelcomeTourFieldAs(true);
                }}
                afterClose={() => {
                    welcomeTourCompleteModalRef.current.open();
                }}
                render={({ closeModal }) => {
                    return (
                        <div style={{ display: 'contents' }}>
                            <div style={{ display: 'grid', gridAutoFlow: 'column' }}>
                                <Component
                                    settings={{ ...reportJSON.reportTemplateSettings, welcomeTourSurveyId }}
                                    hideHeaderInfo
                                    latestTimepointOnly
                                    params={{ contactType: 'provider', pawnId: { creatingPawnId } }}
                                />
                                <div
                                    style={{
                                        position: 'relative',
                                        minWidth: '130px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        style={{
                                            height: 'min-content',
                                            minWidth: '90px',
                                            position: 'sticky',
                                            top: 'calc(100vh - 63px)',
                                        }}
                                        onClick={() => {
                                            closeModal();
                                        }}
                                    >
                                        Got it
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }}
            />
        );
    };

    const ShowWelcomeTourCompleteModal = () => {
        const subtext1 = "Now that you've seen a sample assessment and report, what would you like to do next?";
        const subtext2 =
            'You can add a sample student to try out more assessments and reports or you can start adding real students and administering assessments.';

        return (
            <Modal
                ref={welcomeTourCompleteModalRef}
                size="600px"
                afterShow={() => {
                    setWelcomeTourFieldAs(true);
                }}
                render={({ closeModal }) => {
                    return (
                        <div style={{ padding: '10%', paddingTop: '0px' }}>
                            <img src={ShareLogo} alt="Logo" />
                            <div className={style.welcome_subtext}>
                                <div>{subtext1}</div>
                                <div>{subtext2}</div>
                            </div>
                        </div>
                    );
                }}
            />
        );
    };

    if (href.endsWith('?wt=1')) {
        resetWelcomeTour();
        return <></>;
    }

    if (loading) {
        return (
            <div className={style.loading_container}>
                <Loader loading type="inline" />
            </div>
        );
    }

    return (
        <div style={{ marginLeft: '2%', marginRight: '2%' }}>
            <>
                {ShowWelcomeTour()}
                {ShowWelcomeTourSurveyModal()}
                {ShowWelcomeTourReportModal()}
                {ShowWelcomeTourCompleteModal()}
            </>
            {welcomeTourShown && (
                <>
                    <div>
                        {AddStudentsButton()}
                        {InstructionalArrow()}
                    </div>
                    <div>
                        <br />
                        <br />
                    </div>
                    <div hidden={studentCount <= 0}>
                        <div className={style.studentsTableWrapper}>
                            <div id="students_table_section">{StudentsTable()}</div>
                            <div style={{ marginTop: '25px' }} id="archived_table_section">
                                {ArchivedStudentsTable()}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DeploymentCenter;
